import React from 'react';
import { Box, Chip, Divider, IconButton, InputBase, LinearProgress, List, ListItem, ListItemText, Link, Menu, MenuItem, Paper, Tooltip, Typography, Avatar, Drawer } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { ReactComponent as SendSvg } from '../../assets/images/send.svg';
import {
  agentChatStream,
  agentChatStreamWithFiles,
  chat,
  chatPublic,
  chatWithFiles,
  fileToDataUrl,
  ChatMessage,
  VisionContentPart,
  type AgentMysqlStreamEvent,
} from '../../services/deepseekService';
import { stripGemmaInlineKzmPdfMarker } from '../../utils/gemmaInlineKzmPdf';
import { parseGemmaInlineOptions } from '../../utils/gemmaInlineOptions';
import GemmaInlineOptions from './GemmaInlineOptions';
import VoiceInputButton from './VoiceInputButton';
import { useVoiceDictation } from '../../hooks/useVoiceDictation';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '@mui/material/styles';
import GemmaAvatar from './GemmaAvatar';
import GemmaAgentActivityPanel, { type GemmaActivityLine } from './GemmaAgentActivityPanel';
const BUBBLE_SELF = '#E6F5E6';
const BUBBLE_PEER = '#FFF5CC';
const TEXT_MAIN = '#020242';
const META_KEY = 'ai:gemma:sessions:v1';
const LEGACY_MSGS_KEY = 'ai:msgs:default';

type AIMessage = ChatMessage & { ts?: string };

type SessionMeta = { id: string; updatedAt: string; title: string };

type MetaStore = { activeId: string; list: SessionMeta[] };

const msgsKey = (id: string) => `ai:msgs:${id}`;

function formatYmdHms(iso?: string | null): string {
  if (!iso) return '';
  try {
    const d = new Date(iso as string);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}, ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  } catch {
    return String(iso ?? '');
  }
}

/** Powitanie w nowej sesji (drawer Gemma) — imię/nazwa z profilu lub email. */
function defaultGemmaDrawerGreeting(user: { display_name?: string | null; email?: string | null } | null | undefined): string {
  const label = (user?.display_name || user?.email || '').trim();
  if (label) return `Cześć, ${label}! Jak mogę Ci pomóc?`;
  return 'Cześć! Jak mogę Ci pomóc?';
}

function loadOrInitMeta(): MetaStore {
  try {
    const raw = localStorage.getItem(META_KEY);
    if (raw) {
      const p = JSON.parse(raw);
      if (p?.activeId && Array.isArray(p.list)) return p as MetaStore;
    }
  } catch {}
  try {
    const legacy = localStorage.getItem(LEGACY_MSGS_KEY);
    const id = 'legacy-default';
    if (legacy) {
      localStorage.setItem(msgsKey(id), legacy);
      localStorage.removeItem(LEGACY_MSGS_KEY);
      const ts = new Date().toISOString();
      const meta: MetaStore = { activeId: id, list: [{ id, title: `Rozmowa ${formatYmdHms(ts)}`, updatedAt: ts }] };
      localStorage.setItem(META_KEY, JSON.stringify(meta));
      return meta;
    }
  } catch {}
  const id = `s-${Date.now()}`;
  const sys = [{ role: 'system', content: 'Jesteś pomocnym asystentem Unitresco.' }];
  try {
    localStorage.setItem(msgsKey(id), JSON.stringify(sys));
    const ts = new Date().toISOString();
    const meta: MetaStore = { activeId: id, list: [{ id, title: `Rozmowa ${formatYmdHms(ts)}`, updatedAt: ts }] };
    localStorage.setItem(META_KEY, JSON.stringify(meta));
    return meta;
  } catch {
    const ts = new Date().toISOString();
    return { activeId: id, list: [{ id, title: `Rozmowa ${formatYmdHms(ts)}`, updatedAt: ts }] };
  }
}

function loadMessagesForSession(id: string): AIMessage[] {
  const sys: AIMessage[] = [{ role: 'system', content: 'Jesteś pomocnym asystentem Unitresco.' }];
  try {
    const raw = localStorage.getItem(msgsKey(id));
    if (!raw) return sys;
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr) || arr.length === 0) return sys;
    return arr;
  } catch {
    return sys;
  }
}

const materialSymbolSx = {
  fontFamily: '"Material Symbols Outlined"',
  fontWeight: 400 as const,
  fontStyle: 'normal' as const,
  lineHeight: 1,
  display: 'inline-block',
  color: '#020242',
  fontVariationSettings: "'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24",
};

function displayChatContent(content: ChatMessage['content']): string {
  if (typeof content === 'string') return content;
  return content
    .map((p) => (p.type === 'text' ? p.text : '[obraz]'))
    .filter(Boolean)
    .join('\n') || '[wiadomość multimodalna]';
}

/** Ostatni project_id z odpowiedzi Gemmy, np. (ID: 232-233) lub (ID: `232-233`) — jak w czacie wewnętrznym. */
function extractStickyProjectIdFromAiMessages(msgs: AIMessage[]): string | null {
  const re = /\(ID:\s*`?([0-9A-Za-z][0-9A-Za-z._-]*)`?\)/gi;
  let last: string | null = null;
  for (const m of msgs) {
    if (m.role !== 'assistant') continue;
    const c = displayChatContent(m.content);
    re.lastIndex = 0;
    let ma: RegExpExecArray | null;
    while ((ma = re.exec(c)) !== null) {
      const v = ma[1].trim();
      if (v) last = v;
    }
  }
  return last;
}

const TRIVIAL_TITLE_NORMALIZED = new Set([
  'cześć',
  'czesc',
  'hej',
  'siema',
  'elo',
  'witaj',
  'hi',
  'hello',
  'hey',
  'dzień dobry',
  'dzien dobry',
  'dzieńdobry',
  'ok',
  'oki',
  'tak',
  'nie',
  'dzięki',
  'dzieki',
  'thanks',
  'thx',
  'hmm',
  '?',
]);

function normalizeTitleCandidate(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .replace(/[.!?…]+$/g, '')
    .replace(/\s+/g, ' ');
}

function isTrivialTitleText(s: string): boolean {
  const n = normalizeTitleCandidate(s);
  if (!n || n.length <= 2) return true;
  if (TRIVIAL_TITLE_NORMALIZED.has(n)) return true;
  if (n.length <= 24 && /^[\p{Emoji}\s]+$/u.test(s.trim())) return true;
  return false;
}

/** Tytuł na liście: sensowny skrót pierwszej wiadomości użytkownika albo unikalna etykieta z czasem (bez zbędnego „Rozmowa z Gemmą” / „cześć”). */
function deriveSessionTitle(firstUserContent: ChatMessage['content'] | undefined, updatedAt: string): string {
  if (firstUserContent !== undefined && firstUserContent !== null && firstUserContent !== '') {
    const t = displayChatContent(firstUserContent).trim().replace(/\s+/g, ' ');
    if (t && !isTrivialTitleText(t)) {
      return t.length > 48 ? `${t.slice(0, 48)}…` : t;
    }
  }
  return `Rozmowa ${formatYmdHms(updatedAt)}`;
}

function listItemSecondaryLine(title: string, updatedAt: string): string | undefined {
  const t = formatYmdHms(updatedAt);
  if (!t) return undefined;
  if (title === `Rozmowa ${t}` || title.endsWith(t)) return undefined;
  return t;
}

const LEGACY_MENU_TITLES = new Set(['Rozmowa z Gemmą', 'Nowa rozmowa', 'Rozmowa']);

/** Jednorazowa korekta starych wpisów z localStorage (np. same „cześć” / „Rozmowa z Gemmą”). */
function refreshStaleSessionTitles(meta: MetaStore): MetaStore {
  let changed = false;
  const list = meta.list.map((s) => {
    const needsRefresh =
      LEGACY_MENU_TITLES.has(s.title) || (Boolean(s.title) && isTrivialTitleText(s.title));
    if (!needsRefresh) return s;
    const msgs = loadMessagesForSession(s.id);
    const firstUser = msgs.find((m) => m.role === 'user');
    const title = deriveSessionTitle(firstUser?.content, s.updatedAt);
    if (title !== s.title) changed = true;
    return { ...s, title };
  });
  if (changed) {
    try {
      localStorage.setItem(META_KEY, JSON.stringify({ activeId: meta.activeId, list }));
    } catch {}
  }
  return { activeId: meta.activeId, list };
}

const linkSx = {
  color: 'inherit',
  fontWeight: 600,
  wordBreak: 'break-all' as const,
};

/** Markdown [label](https://…) oraz gołe URL-e http(s) → klikalne MUI Link. */
function renderMarkdownLinksAndUrls(text: string, msgIdx: number): React.ReactNode {
  let keyCounter = 0;
  const nextKey = (prefix: string) => `${msgIdx}-${prefix}-${keyCounter++}`;

  const renderBareUrls = (segment: string): React.ReactNode[] => {
    if (!segment) return [];
    const parts: React.ReactNode[] = [];
    const urlRe = /https?:\/\/[^\s<>"'[\]]+/g;
    let pos = 0;
    let m: RegExpExecArray | null;
    while ((m = urlRe.exec(segment)) !== null) {
      if (m.index > pos) parts.push(segment.slice(pos, m.index));
      const raw = m[0];
      const url = raw.replace(/[.,;:!?)}\]]+$/, '');
      const advance = raw.length;
      if (url && /^https?:\/\//i.test(url)) {
        parts.push(
          <Link key={nextKey('u')} href={url} target="_blank" rel="noopener noreferrer" underline="always" sx={linkSx}>
            {url}
          </Link>
        );
      } else if (raw) {
        parts.push(raw);
      }
      pos = m.index + advance;
    }
    if (pos < segment.length) parts.push(segment.slice(pos));
    return parts;
  };

  const out: React.ReactNode[] = [];
  const mdRe = /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/gi;
  let pos = 0;
  let m: RegExpExecArray | null;
  while ((m = mdRe.exec(text)) !== null) {
    if (m.index > pos) out.push(...renderBareUrls(text.slice(pos, m.index)));
    const href = m[2];
    if (/^https?:\/\//i.test(href)) {
      out.push(
        <Link key={nextKey('m')} href={href} target="_blank" rel="noopener noreferrer" underline="always" sx={linkSx}>
          {m[1]}
        </Link>
      );
    } else {
      out.push(m[0]);
    }
    pos = mdRe.lastIndex;
  }
  if (pos < text.length) out.push(...renderBareUrls(text.slice(pos)));
  if (out.length === 0) return text;
  return out;
}

interface AIDialogProps {
  open: boolean;
  onClose: () => void;
  zIndex?: number;
  onEntered?: () => void;
}

const AIDialog: React.FC<AIDialogProps> = ({ open, onClose, zIndex, onEntered }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const drawerStackZ = typeof zIndex === 'number' ? zIndex : theme.zIndex.drawer;
  const historyMenuZ = drawerStackZ + 2;
  const [messages, setMessages] = React.useState<AIMessage[]>([{ role: 'system', content: 'Jesteś pomocnym asystentem Unitresco.' }]);
  const [input, setInput] = React.useState('');
  const voiceDictation = useVoiceDictation({
    lang: 'pl-PL',
    value: input,
    onChangeValue: setInput,
  });
  /** Pliki z dolnego spinacza / drag&drop — kontekst rozmowy (nie staging KZM). */
  const [contextFiles, setContextFiles] = React.useState<File[]>([]);
  /** Tylko PDF z przycisku „Dodaj PDF do KZM” w bąbelku — `agentChatStreamWithFiles`. */
  const [kzmPdfFiles, setKzmPdfFiles] = React.useState<File[]>([]);
  const [loading, setLoading] = React.useState(false);
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const gemmaDeltaRafRef = React.useRef<number | null>(null);
  const gemmaDeltaAccumRef = React.useRef('');
  const gemmaDeltaTargetTsRef = React.useRef<string | null>(null);
  const textInputRef = React.useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const kzmPdfOnlyInputRef = React.useRef<HTMLInputElement | null>(null);
  const [provider, setProvider] = React.useState<string>('deepseek');
  const [model, setModel] = React.useState<string>('deepseek-chat');
  const [sessionId, setSessionId] = React.useState<string>('');
  const [sessionsList, setSessionsList] = React.useState<SessionMeta[]>([]);
  const [metaHydrated, setMetaHydrated] = React.useState(false);
  const [historyAnchor, setHistoryAnchor] = React.useState<null | HTMLElement>(null);
  const [gemmaThinking, setGemmaThinking] = React.useState(false);
  const [gemmaActivityLines, setGemmaActivityLines] = React.useState<GemmaActivityLine[]>([]);
  const [gemmaActivityExpanded, setGemmaActivityExpanded] = React.useState(false);

  const pushGemmaActivityLine = React.useCallback((verb: string, detail?: string) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    setGemmaActivityLines((prev) =>
      [...prev.map((l) => ({ ...l, isLive: false })), { id, verb, detail, isLive: true }].slice(-35),
    );
  }, []);

  React.useEffect(() => {
    return () => {
      if (gemmaDeltaRafRef.current != null) {
        cancelAnimationFrame(gemmaDeltaRafRef.current);
        gemmaDeltaRafRef.current = null;
      }
    };
  }, []);

  const userLabel = (user?.display_name || user?.email || 'Ty').trim() || 'Ty';
  const userRef = React.useRef(user);
  userRef.current = user;

  React.useEffect(() => {
    const meta = refreshStaleSessionTitles(loadOrInitMeta());
    setSessionId(meta.activeId);
    setMessages(loadMessagesForSession(meta.activeId));
    setSessionsList(meta.list);
    setMetaHydrated(true);
  }, []);

  React.useEffect(() => {
    if (!metaHydrated || !sessionId) return;
    try {
      localStorage.setItem(msgsKey(sessionId), JSON.stringify(messages));
      const firstUser = messages.find((m) => m.role === 'user');
      const updatedAt = new Date().toISOString();
      const title = deriveSessionTitle(firstUser?.content, updatedAt);
      const metaRaw = localStorage.getItem(META_KEY);
      const meta: MetaStore = metaRaw ? JSON.parse(metaRaw) : { activeId: sessionId, list: [] };
      let list = [...meta.list];
      const idx = list.findIndex((s) => s.id === sessionId);
      if (idx >= 0) list[idx] = { ...list[idx], title, updatedAt };
      else list = [{ id: sessionId, title, updatedAt }, ...list];
      localStorage.setItem(META_KEY, JSON.stringify({ activeId: meta.activeId, list }));
      setSessionsList(list);
    } catch {}
  }, [messages, sessionId, metaHydrated]);

  React.useEffect(() => {
    if (open) {
      setTimeout(() => {
        setMessages(prev => {
          const hasGreeting = prev.some(m => m.role !== 'system');
          if (hasGreeting) return prev;
          const ts = new Date().toISOString();
          return [...prev, { role: 'assistant', content: defaultGemmaDrawerGreeting(userRef.current), ts }];
        });
      }, 0);
      try {
        const p = localStorage.getItem('ai:provider') || 'deepseek';
        const m = localStorage.getItem(`ai:model:${p}`) || (p==='deepseek' ? 'deepseek-chat' : p==='spark' ? 'gemma-4-31B-it' : 'claude-3-5-sonnet-latest');
        setProvider(p);
        setModel(m);
      } catch {}
    }
  }, [open]);

  React.useEffect(() => {
    function onSettingsUpdate(){
      try {
        const p = localStorage.getItem('ai:provider') || 'deepseek';
        const m = localStorage.getItem(`ai:model:${p}`) || (p==='deepseek' ? 'deepseek-chat' : p==='spark' ? 'gemma-4-31B-it' : 'claude-3-5-sonnet-latest');
        setProvider(p);
        setModel(m);
      } catch {}
    }
    window.addEventListener('ai:settings:changed', onSettingsUpdate as any);
    return () => window.removeEventListener('ai:settings:changed', onSettingsUpdate as any);
  }, []);

  React.useEffect(() => {
    try { scrollRef.current?.scrollTo({ top: 999999, behavior: 'smooth' }); } catch {}
  }, [messages.length, loading]);

  React.useEffect(() => {
    if (open) {
      try { setTimeout(() => { textInputRef.current?.focus(); }, 0); } catch {}
    }
  }, [open]);

  const MAX_KZM_CHAT_PDF = 8;

  const onPickContextFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fl = Array.from(e.target.files || []);
    setContextFiles((prev) => [...prev, ...fl]);
    e.target.value = '';
  };

  const onPickKzmPdfFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fl = Array.from(e.target.files || []).filter(
      (f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'),
    );
    setKzmPdfFiles((prev) => {
      const next = [...prev, ...fl];
      return next.slice(0, MAX_KZM_CHAT_PDF);
    });
    e.target.value = '';
  };

  const removeContextFile = (idx: number) => setContextFiles((prev) => prev.filter((_, i) => i !== idx));
  const removeKzmPdfFile = (idx: number) => setKzmPdfFiles((prev) => prev.filter((_, i) => i !== idx));

  const nowIso = () => new Date().toISOString();

  async function send(overrideText?: string) {
    const text = (overrideText ?? input).trim();
    if (!text && contextFiles.length === 0 && kzmPdfFiles.length === 0) return;

    if (kzmPdfFiles.length > 0 && contextFiles.length > 0) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content:
            'W jednej wiadomości nie można mieszać plików kontekstu (dolny spinacz) z PDF pod KZM (przycisk w wiadomości Gemmy). Usuń jedną z grup albo wyślij w dwóch krokach.',
          ts: nowIso(),
        },
      ]);
      return;
    }

    setLoading(true);
    try { window.dispatchEvent(new CustomEvent('ai:busy', { detail: { busy: true } })); } catch {}
    if (overrideText === undefined) setInput('');
    try {
      let reply = '';
      let skipFinalAssistant = false;
      let streamAssistMarker: string | null = null;
      const imageFiles = contextFiles.filter((f) => f.type.startsWith('image/'));
      const docFiles = contextFiles.filter((f) => !f.type.startsWith('image/'));
      const isSpark = provider === 'spark';
      const authToken = (() => { try { return localStorage.getItem('auth_token'); } catch { return null; } })();
      const useSqlAgent = Boolean(
        isSpark && authToken && imageFiles.length === 0 && docFiles.length === 0 && kzmPdfFiles.length === 0,
      );

      const flushGemmaDeltaToUi = (tsKey: string) => {
        gemmaDeltaRafRef.current = null;
        if (gemmaDeltaTargetTsRef.current !== tsKey) return;
        const t = gemmaDeltaAccumRef.current;
        setMessages((prev) =>
          prev.map((m) => (m.role === 'assistant' && m.ts === tsKey ? { ...m, content: t } : m)),
        );
        try {
          scrollRef.current?.scrollTo({ top: 999999, behavior: 'auto' });
        } catch {
          /* noop */
        }
      };

      const onAgentStream =
        (mark: string | null) =>
        (ev: AgentMysqlStreamEvent): void => {
          if (ev.type === 'assistant_delta' && ev.text && mark) {
            gemmaDeltaAccumRef.current += ev.text;
            if (gemmaDeltaRafRef.current == null) {
              gemmaDeltaRafRef.current = requestAnimationFrame(() => flushGemmaDeltaToUi(mark));
            }
            return;
          }
          if (ev.type === 'assistant_stream_reset' && mark) {
            if (gemmaDeltaRafRef.current != null) {
              cancelAnimationFrame(gemmaDeltaRafRef.current);
              gemmaDeltaRafRef.current = null;
            }
            gemmaDeltaAccumRef.current = '';
            setMessages((prev) =>
              prev.map((m) => (m.role === 'assistant' && m.ts === mark ? { ...m, content: '' } : m)),
            );
            return;
          }
          if (ev.type === 'started') pushGemmaActivityLine('Start', `Do ${ev.maxIterations} rund z narzędziami`);
          if (ev.type === 'thinking') pushGemmaActivityLine('Thinking', `Runda ${ev.iteration} / ${ev.maxIterations} — model`);
          if (ev.type === 'tool_calls') pushGemmaActivityLine('Tool', ev.tools.join(', '));
          if (ev.type === 'mysql_read_query') pushGemmaActivityLine('Read mysql_read_query', ev.preview);
          if (ev.type === 'mysql_read_query_done') {
            pushGemmaActivityLine(ev.ok ? 'SQL — wykonano' : 'SQL — błąd', ev.message);
          }
          if (ev.type === 'read_context_file') pushGemmaActivityLine('read_context_file', ev.preview || ev.path);
          if (ev.type === 'read_context_file_done') {
            pushGemmaActivityLine(
              ev.ok ? 'Plik kontekstu — wczytano' : 'Plik kontekstu — błąd',
              ev.ok ? ev.path || '—' : ev.message,
            );
          }
          if (ev.type === 'kzm_quick_create') pushGemmaActivityLine('kzm_quick_create', ev.preview);
          if (ev.type === 'kzm_quick_create_done') {
            pushGemmaActivityLine(
              ev.ok ? 'KZM — utworzono' : 'KZM — błąd',
              ev.ok
                ? `HTTP ${ev.httpStatus ?? '—'}`
                : `${ev.httpStatus != null ? `HTTP ${ev.httpStatus}: ` : ''}${ev.message || '—'}`,
            );
          }
          if (ev.type === 'kzm_staging_upload') pushGemmaActivityLine('Staging PDF', `${ev.count} plik(ów)`);
          if (ev.type === 'kzm_staging_upload_done') {
            pushGemmaActivityLine(ev.ok ? 'Staging — OK' : 'Staging — błąd', ev.message);
          }
          if (ev.type === 'unknown_tool') pushGemmaActivityLine('Nieznane narzędzie', ev.name || '—');
          if (ev.type === 'error') pushGemmaActivityLine('Błąd', ev.message);
        };

      const runSparkSqlStreamOrPublic = async (base: ChatMessage[], maxTok: number): Promise<string> => {
        if (!useSqlAgent) {
          return chatPublic(base, { max_tokens: maxTok, model });
        }
        const assistStreamTs = nowIso();
        streamAssistMarker = assistStreamTs;
        gemmaDeltaAccumRef.current = '';
        gemmaDeltaTargetTsRef.current = assistStreamTs;
        if (gemmaDeltaRafRef.current != null) {
          cancelAnimationFrame(gemmaDeltaRafRef.current);
          gemmaDeltaRafRef.current = null;
        }
        setMessages((prev) => [...prev, { role: 'assistant', content: '', ts: assistStreamTs }]);
        setGemmaThinking(true);
        setGemmaActivityLines([]);
        setGemmaActivityExpanded(false);
        try {
          const out = await agentChatStream(base, { max_tokens: maxTok, model }, onAgentStream(assistStreamTs));
          if (gemmaDeltaRafRef.current != null) {
            cancelAnimationFrame(gemmaDeltaRafRef.current);
            gemmaDeltaRafRef.current = null;
          }
          gemmaDeltaAccumRef.current = '';
          gemmaDeltaTargetTsRef.current = null;
          return out;
        } catch (streamErr) {
          if (gemmaDeltaRafRef.current != null) {
            cancelAnimationFrame(gemmaDeltaRafRef.current);
            gemmaDeltaRafRef.current = null;
          }
          gemmaDeltaAccumRef.current = '';
          gemmaDeltaTargetTsRef.current = null;
          const em = String((streamErr as Error)?.message || '');
          const is503 = /^HTTP\s*503\b/i.test(em);
          setMessages((prev) => prev.filter((m) => !(m.role === 'assistant' && m.ts === assistStreamTs)));
          if (is503) {
            streamAssistMarker = null;
            return chatPublic(base, { max_tokens: maxTok, model });
          }
          throw streamErr;
        } finally {
          setGemmaThinking(false);
          setGemmaActivityLines([]);
        }
      };

      if (kzmPdfFiles.length > 0) {
        if (!isSpark) {
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              content: 'PDF pod KZM ze stagingiem na Drive jest dostępny przy wyborze modelu **Spark** (Gemma).',
              ts: nowIso(),
            },
          ]);
          skipFinalAssistant = true;
        } else if (!authToken) {
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              content: 'Wgrywanie PDF pod KZM wymaga zalogowania.',
              ts: nowIso(),
            },
          ]);
          skipFinalAssistant = true;
        } else {
          const userPreview = text || '(załączono PDF pod KZM)';
          setMessages((prev) => [...prev, { role: 'user', content: userPreview, ts: nowIso() }]);
          const stickyPid = extractStickyProjectIdFromAiMessages(messages);
          const body = text || '(załączono PDF)';
          const contentForApi =
            stickyPid != null && stickyPid.length > 0
              ? `${body}\n\n[System / backend: W tej rozmowie wcześniej ustalono **project_id=${stickyPid}**. Przy \`kzm_quick_create\` użyj **dokładnie tego** \`project_id\`, dopóki użytkownik wyraźnie nie wskaże innego projektu.]`
              : body;
          const base = [...messages, { role: 'user', content: contentForApi, ts: nowIso() }] as ChatMessage[];
          const assistStreamTs = nowIso();
          streamAssistMarker = assistStreamTs;
          gemmaDeltaAccumRef.current = '';
          gemmaDeltaTargetTsRef.current = assistStreamTs;
          if (gemmaDeltaRafRef.current != null) {
            cancelAnimationFrame(gemmaDeltaRafRef.current);
            gemmaDeltaRafRef.current = null;
          }
          setMessages((prev) => [...prev, { role: 'assistant', content: '', ts: assistStreamTs }]);
          setGemmaThinking(true);
          setGemmaActivityLines([]);
          setGemmaActivityExpanded(false);
          try {
            reply = await agentChatStreamWithFiles(
              base,
              kzmPdfFiles,
              { max_tokens: 4096, model },
              onAgentStream(streamAssistMarker),
            );
          } finally {
            if (gemmaDeltaRafRef.current != null) {
              cancelAnimationFrame(gemmaDeltaRafRef.current);
              gemmaDeltaRafRef.current = null;
            }
            gemmaDeltaAccumRef.current = '';
            gemmaDeltaTargetTsRef.current = null;
            setGemmaThinking(false);
            setGemmaActivityLines([]);
          }
        }
      } else if (isSpark && (imageFiles.length > 0 || (contextFiles.length === 0 && text))) {
        if (docFiles.length > 0) {
          setMessages(prev => [...prev, { role: 'assistant', content: 'Przy Spark Gemma wyślij albo obrazy (PNG, JPEG, WebP…), albo pliki PDF/DOCX — nie oba typy w jednej wiadomości. PDF/DOCX wymagają zalogowania.', ts: nowIso() }]);
          skipFinalAssistant = true;
        } else if (imageFiles.length > 0) {
          const parts: VisionContentPart[] = [];
          if (text) parts.push({ type: 'text', text });
          for (const img of imageFiles) {
            const url = await fileToDataUrl(img);
            parts.push({ type: 'image_url', image_url: { url } });
          }
          if (parts.length === 0) parts.push({ type: 'text', text: '(obraz)' });
          const userMsg: AIMessage = { role: 'user', content: parts, ts: nowIso() };
          const preview = text || (imageFiles.length ? `[${imageFiles.length} obraz(y)]` : '');
          setMessages(prev => [...prev, { role: 'user', content: preview || '(wiadomość z obrazem)', ts: userMsg.ts }]);
          const base = [...messages, userMsg] as ChatMessage[];
          reply = await chatPublic(base, { max_tokens: 2048, model });
        } else {
          const userMsg: AIMessage = { role: 'user', content: text, ts: nowIso() };
          setMessages(prev => [...prev, userMsg]);
          const base = [...messages, userMsg] as ChatMessage[];
          reply = await runSparkSqlStreamOrPublic(base, 2048);
        }
      } else if (isSpark && docFiles.length > 0) {
        const token = (() => { try { return localStorage.getItem('auth_token'); } catch { return null; } })();
        if (!token) {
          setMessages(prev => [...prev, { role: 'assistant', content: 'Analiza PDF/DOCX przez backend wymaga zalogowania. Możesz bez logowania wysłać obraz (PNG, JPEG) do modelu Spark Gemma.', ts: nowIso() }]);
          skipFinalAssistant = true;
        } else {
          const userPreview = text || '(załączono pliki)';
          setMessages((prev) => [...prev, { role: 'user', content: userPreview, ts: nowIso() }]);
          reply = await chatWithFiles({ prompt: text || 'Przeanalizuj załączone pliki i podaj wnioski.', files: docFiles });
        }
      } else if (contextFiles.length > 0) {
        setMessages(prev => [...prev, { role: 'user', content: text || '(załączono pliki)', ts: nowIso() }]);
        reply = await chatWithFiles({ prompt: text || 'Przeanalizuj załączone pliki i podaj wnioski.', files: contextFiles });
      } else {
        const userMsg: AIMessage = { role: 'user', content: text, ts: nowIso() };
        setMessages(prev => [...prev, userMsg]);
        const base = [...messages, userMsg] as ChatMessage[];
        if (isSpark) {
          reply = await runSparkSqlStreamOrPublic(base, 512);
        } else {
          reply = await chat(base, { max_tokens: 512 });
        }
      }
      if (!skipFinalAssistant) {
        if (streamAssistMarker) {
          setMessages((prev) =>
            prev.map((m) =>
              m.role === 'assistant' && m.ts === streamAssistMarker
                ? { ...m, content: reply, ts: nowIso() }
                : m,
            ),
          );
        } else {
          setMessages((prev) => [...prev, { role: 'assistant', content: reply, ts: nowIso() }]);
        }
        try { window.dispatchEvent(new CustomEvent('ai:ready', { detail: { message: reply } })); } catch {}
      }
      setContextFiles([]);
      setKzmPdfFiles([]);
    } catch (e) {
      console.error('AI error', e);
      const code = (e as any)?.code || '';
      const message = (e as any)?.response?.data?.message || (e as any)?.message || 'Błąd zapytania.';
      if (code === 'ERR_CANCELED' || /abort/i.test(String(message))) {
        // pomiń
      } else {
        setMessages((prev) => {
          const cleaned = prev.filter(
            (m) => !(m.role === 'assistant' && displayChatContent(m.content).trim() === ''),
          );
          return [...cleaned, { role: 'assistant', content: `Błąd: ${message}`, ts: nowIso() }];
        });
      }
    }
    setLoading(false);
    try { window.dispatchEvent(new CustomEvent('ai:busy', { detail: { busy: false } })); } catch {}
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    const fl = Array.from(e.dataTransfer.files || []);
    if (fl.length) setContextFiles((prev) => [...prev, ...fl]);
  }

  function onDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
  }

  function copy(text: string) {
    try { navigator.clipboard.writeText(text); } catch {}
  }

  function clearChat() {
    const sys: AIMessage[] = [{ role: 'system', content: 'Jesteś pomocnym asystentem Unitresco.' }];
    setMessages(sys);
    setContextFiles([]);
    setKzmPdfFiles([]);
    if (sessionId) try { localStorage.setItem(msgsKey(sessionId), JSON.stringify(sys)); } catch {}
  }

  function newChat() {
    const newId = `s-${Date.now()}`;
    try {
      const metaRaw = localStorage.getItem(META_KEY);
      const meta: MetaStore = metaRaw ? JSON.parse(metaRaw) : { activeId: sessionId, list: sessionsList };
      const ts = new Date().toISOString();
      const list = [{ id: newId, title: deriveSessionTitle(undefined, ts), updatedAt: ts }, ...meta.list.filter((x) => x.id !== newId)];
      localStorage.setItem(META_KEY, JSON.stringify({ activeId: newId, list }));
      const sys: AIMessage[] = [{ role: 'system', content: 'Jesteś pomocnym asystentem Unitresco.' }];
      localStorage.setItem(msgsKey(newId), JSON.stringify(sys));
      setSessionId(newId);
      setMessages(sys);
      setSessionsList(list);
      setHistoryAnchor(null);
      setTimeout(() => {
        setMessages((prev) => {
          if (prev.some((m) => m.role !== 'system')) return prev;
          return [...prev, { role: 'assistant', content: defaultGemmaDrawerGreeting(user), ts: new Date().toISOString() }];
        });
      }, 0);
    } catch {}
  }

  function selectSession(id: string) {
    if (id === sessionId) {
      setHistoryAnchor(null);
      return;
    }
    try {
      const next = loadMessagesForSession(id);
      setMessages(next);
      setSessionId(id);
      const metaRaw = localStorage.getItem(META_KEY);
      const meta: MetaStore = metaRaw ? JSON.parse(metaRaw) : { activeId: id, list: sessionsList };
      localStorage.setItem(META_KEY, JSON.stringify({ ...meta, activeId: id }));
      setHistoryAnchor(null);
    } catch {}
  }

  function deleteSession(id: string) {
    if (!id) return;
    try {
      const metaRaw = localStorage.getItem(META_KEY);
      const meta: MetaStore = metaRaw ? JSON.parse(metaRaw) : { activeId: sessionId, list: sessionsList };
      let list = meta.list.filter((x) => x.id !== id);
      localStorage.removeItem(msgsKey(id));

      const appendGreeting = () => {
        setTimeout(() => {
          setMessages((prev) => {
            if (prev.some((m) => m.role !== 'system')) return prev;
            return [...prev, { role: 'assistant', content: defaultGemmaDrawerGreeting(user), ts: new Date().toISOString() }];
          });
        }, 0);
      };

      if (list.length === 0) {
        const newId = `s-${Date.now()}`;
        const ts = new Date().toISOString();
        const sys: AIMessage[] = [{ role: 'system', content: 'Jesteś pomocnym asystentem Unitresco.' }];
        localStorage.setItem(msgsKey(newId), JSON.stringify(sys));
        list = [{ id: newId, title: deriveSessionTitle(undefined, ts), updatedAt: ts }];
        localStorage.setItem(META_KEY, JSON.stringify({ activeId: newId, list }));
        setSessionId(newId);
        setMessages(sys);
        setSessionsList(list);
        appendGreeting();
        return;
      }

      let nextActive = meta.activeId;
      if (id === sessionId) {
        nextActive = list[0].id;
        setSessionId(nextActive);
        setMessages(loadMessagesForSession(nextActive));
      } else if (meta.activeId === id) {
        nextActive = list[0].id;
      }
      localStorage.setItem(META_KEY, JSON.stringify({ activeId: nextActive, list }));
      setSessionsList(list);
    } catch {}
  }

  const currentSessionTitle = React.useMemo(() => {
    const s = sessionsList.find((x) => x.id === sessionId);
    return s?.title || 'Rozmowa z Gemmą';
  }, [sessionsList, sessionId]);

  const canSend = (input.trim().length > 0 || contextFiles.length > 0 || kzmPdfFiles.length > 0) && !loading;
  const sendColor = canSend ? '#3F3FDC' : '#B9B9B9';
  const providerCaption = provider === 'anthropic' ? 'Anthropic' : provider === 'spark' ? 'Spark' : 'DeepSeek';

  return (
    <Drawer
      variant="temporary"
      anchor="right"
      open={open}
      onClose={onClose}
      SlideProps={{ onEntered: () => { try { onEntered?.(); } catch {} } } as any}
      sx={{ zIndex: zIndex ?? ((theme) => theme.zIndex.drawer) }}
      ModalProps={{ keepMounted: true, disableEnforceFocus: true, disableAutoFocus: true, BackdropProps: { sx: { backgroundColor: 'rgba(0,0,0,0.35)' } } }}
      PaperProps={{
        sx: {
          width: 420,
          maxWidth: '100vw',
          flexShrink: 0,
          top: 0,
          height: '100vh',
          bottom: 0,
          borderRadius: 0,
          overflow: 'hidden',
          boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
          right: 0,
          pointerEvents: 'auto',
          zIndex: zIndex ?? (theme => theme.zIndex.drawer)
        },
        onTransitionEnd: (e: React.TransitionEvent<HTMLDivElement>) => { try { if ((e as any).propertyName === 'transform') onEntered?.(); } catch {} }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          /* iOS: treść (w tym pasek z ikonkami) nie wpada pod home indicator; desktop: 0 */
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#fff', borderBottom: '1px solid #eee' }}>
          <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box component="img" src="/assets/topbar/chat.svg" alt="chat" sx={{ width: 20, height: 20 }} />
              <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '18px', color: '#2D2D32' }}>Chat</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Tooltip title="Wyczyść rozmowę">
                <IconButton size="small" onClick={clearChat} aria-label="Wyczyść">
                  <DeleteForeverIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
              <IconButton onClick={onClose} aria-label="Zamknij">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </Box>

        <Box sx={{ px: 2, py: 1, mt: 2, display: 'flex', alignItems: 'center', gap: 1, minHeight: 38 }}>
          <Box
            component="img"
            src="/assets/icons/arrow-right.svg"
            alt="Wróć"
            sx={{ width: 10, height: 10, cursor: 'pointer', transform: 'rotate(180deg)', transition: 'transform 120ms ease', flexShrink: 0 }}
            onClick={onClose}
          />
          <Tooltip title={`${providerCaption} · ${model}`} arrow placement="bottom">
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: 'Roboto, sans-serif',
                fontWeight: 500,
                fontSize: '14px',
                color: '#020242',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
                minWidth: 0,
              }}
            >
              {currentSessionTitle}
            </Typography>
          </Tooltip>
          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, gap: 0.25, ml: 0.5 }}>
            <Tooltip title="Historia czatów" arrow>
              <IconButton
                size="small"
                onClick={(e) => setHistoryAnchor(e.currentTarget)}
                aria-label="Historia czatów"
                sx={{ p: 0.75 }}
              >
                <Box
                  component="span"
                  className="material-symbols-outlined"
                  sx={{ ...materialSymbolSx, fontSize: 22 }}
                  aria-hidden
                >
                  view_sidebar
                </Box>
              </IconButton>
            </Tooltip>
            <Tooltip title="Nowy chat" arrow>
              <IconButton size="small" onClick={() => newChat()} aria-label="Nowy chat" sx={{ p: 0.75 }}>
                <Box component="span" className="material-symbols-outlined" sx={{ ...materialSymbolSx, fontSize: 24 }} aria-hidden>
                  add
                </Box>
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Menu
          anchorEl={historyAnchor}
          open={Boolean(historyAnchor)}
          onClose={() => setHistoryAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{ zIndex: historyMenuZ }}
          PaperProps={{ sx: { maxHeight: 320, width: 280 } }}
        >
          {[...sessionsList]
            .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
            .map((s) => {
              const primary = s.title || `Rozmowa ${formatYmdHms(s.updatedAt)}`;
              return (
                <MenuItem
                  key={s.id}
                  selected={s.id === sessionId}
                  onClick={() => selectSession(s.id)}
                  sx={{
                    py: 1,
                    pr: 0.5,
                    alignItems: 'flex-start',
                    display: 'flex',
                    gap: 0.5,
                    overflow: 'hidden',
                  }}
                >
                  <ListItemText
                    primary={primary}
                    secondary={listItemSecondaryLine(primary, s.updatedAt)}
                    primaryTypographyProps={{
                      variant: 'body2',
                      noWrap: true,
                      sx: {
                        fontWeight: s.id === sessionId ? 600 : 400,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      },
                    }}
                    secondaryTypographyProps={{
                      variant: 'caption',
                      noWrap: true,
                      sx: { overflow: 'hidden', textOverflow: 'ellipsis' },
                    }}
                    sx={{
                      flex: '1 1 auto',
                      minWidth: 0,
                      maxWidth: '100%',
                      mr: 0.5,
                      overflow: 'hidden',
                      '& .MuiListItemText-primary': { display: 'block' },
                      '& .MuiListItemText-secondary': { display: 'block' },
                    }}
                  />
                  <Tooltip title="Usuń rozmowę">
                    <IconButton
                      size="small"
                      aria-label="Usuń rozmowę"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteSession(s.id);
                      }}
                      sx={{ alignSelf: 'flex-start', mt: 0.25, flexShrink: 0, ml: 'auto' }}
                    >
                      <Box
                        component="span"
                        className="material-symbols-outlined"
                        sx={{ ...materialSymbolSx, fontSize: 20 }}
                        aria-hidden
                      >
                        delete_outline
                      </Box>
                    </IconButton>
                  </Tooltip>
                </MenuItem>
              );
            })}
        </Menu>
        <Divider />

        <Box onDrop={onDrop} onDragOver={onDragOver} sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Box ref={scrollRef} sx={{ flex: 1, overflowY: 'auto', py: 1, px: 2 }}>
            <List dense>
              {(() => {
                const visibleMessages = messages.filter(m => m.role !== 'system');
                let lastAssistantVisibleIdx = -1;
                for (let i = visibleMessages.length - 1; i >= 0; i--) {
                  if (visibleMessages[i].role === 'assistant') { lastAssistantVisibleIdx = i; break; }
                }
                return visibleMessages.map((m, idx) => {
                const isUser = m.role === 'user';
                const bubbleBg = isUser ? BUBBLE_SELF : BUBBLE_PEER;
                const rawBubbleText = displayChatContent(m.content);
                const { displayText: afterPdfStrip, showInlinePdfAttach } =
                  !isUser && provider === 'spark'
                    ? stripGemmaInlineKzmPdfMarker(rawBubbleText)
                    : { displayText: rawBubbleText, showInlinePdfAttach: false };
                const { displayText: assistantDisplayForRender, groups: inlineOptionGroups } =
                  !isUser && provider === 'spark'
                    ? parseGemmaInlineOptions(afterPdfStrip)
                    : { displayText: afterPdfStrip, groups: [] as ReturnType<typeof parseGemmaInlineOptions>['groups'] };
                const showInlineOptions = !isUser && provider === 'spark' && inlineOptionGroups.length > 0 && idx === lastAssistantVisibleIdx;
                const bubbleTextForCopy = !isUser && provider === 'spark' ? assistantDisplayForRender : rawBubbleText;
                return (
                  <ListItem key={idx} alignItems="flex-start" sx={{ px: 0, py: 0 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        px: 0.5,
                        py: 0.75,
                        mb: 0.5,
                        borderRadius: 1,
                        alignItems: isUser ? 'flex-end' : 'flex-start',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: isUser ? 'flex-end' : 'flex-start', flexDirection: 'row', whiteSpace: 'nowrap', minWidth: 0 }}>
                        {isUser ? (
                          <>
                            <Avatar src={user?.avatar_url || undefined} sx={{ width: 20, height: 20 }}>
                              {!user?.avatar_url ? userLabel.slice(0, 1).toUpperCase() : null}
                            </Avatar>
                            <Typography variant="body2" sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: '#2D2D32', fontWeight: 500, whiteSpace: 'nowrap', flexShrink: 0 }}>
                              {userLabel}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                              {formatYmdHms(m.ts)}
                            </Typography>
                          </>
                        ) : (
                          <>
                            <GemmaAvatar size={20} />
                            <Typography variant="body2" sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: '#2D2D32', fontWeight: 500, whiteSpace: 'nowrap', flexShrink: 0 }}>
                              Gemma
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                              {formatYmdHms(m.ts)}
                            </Typography>
                          </>
                        )}
                      </Box>
                      <Box sx={{ mt: 0.25, display: 'inline-flex', flexDirection: 'column', gap: 0.75, alignSelf: isUser ? 'flex-end' : 'flex-start', maxWidth: { xs: '85%', sm: '75%' } }}>
                        <Box sx={{ bgcolor: bubbleBg, color: TEXT_MAIN, px: 1.25, py: 0.9, borderRadius: '4px', boxShadow: '0 1px 2px rgba(0,0,0,0.06)' }}>
                          <Typography component="div" variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                            {renderMarkdownLinksAndUrls(isUser ? rawBubbleText : assistantDisplayForRender, idx)}
                          </Typography>
                          {!isUser && showInlinePdfAttach && provider === 'spark' && user && (
                            <Box
                              sx={{
                                mt: 1,
                                pt: 1,
                                borderTop: '1px solid rgba(2,2,66,0.12)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                flexWrap: 'wrap',
                              }}
                            >
                              <Box
                                component="button"
                                type="button"
                                onClick={() => {
                                  if (!loading) kzmPdfOnlyInputRef.current?.click();
                                }}
                                disabled={loading}
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.75,
                                  border: '1px solid rgba(63,63,220,0.35)',
                                  borderRadius: '6px',
                                  backgroundColor: 'rgba(63,63,220,0.06)',
                                  color: TEXT_MAIN,
                                  cursor: loading ? 'default' : 'pointer',
                                  py: 0.5,
                                  px: 1,
                                  font: 'inherit',
                                  fontSize: 13,
                                  '&:hover': loading ? {} : { backgroundColor: 'rgba(63,63,220,0.1)' },
                                }}
                              >
                                <AttachFileIcon sx={{ fontSize: 18, transform: 'rotate(45deg)', color: '#6B6BD6' }} />
                                <Typography component="span" variant="body2" sx={{ fontWeight: 500 }}>
                                  Dodaj PDF do KZM
                                </Typography>
                              </Box>
                            </Box>
                          )}
                          {showInlineOptions && (
                            <GemmaInlineOptions
                              groups={inlineOptionGroups}
                              disabled={loading}
                              onPick={(value) => {
                                if (loading) return;
                                void send(value);
                              }}
                            />
                          )}
                          {!isUser && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                              <Tooltip title="Kopiuj">
                                <IconButton size="small" onClick={() => copy(bubbleTextForCopy)} sx={{ color: 'inherit' }}>
                                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </ListItem>
                );
                });
              })()}
            </List>
            {gemmaThinking && (
              <Box sx={{ mt: 0.5, px: 0.5 }}>
                {gemmaActivityLines.length > 0 && (
                  <GemmaAgentActivityPanel
                    lines={gemmaActivityLines}
                    expanded={gemmaActivityExpanded}
                    onToggle={() => setGemmaActivityExpanded((v) => !v)}
                  />
                )}
                <LinearProgress sx={{ mt: gemmaActivityLines.length > 0 ? 0.75 : 0 }} />
              </Box>
            )}
            {kzmPdfFiles.length > 0 && (
              <Box sx={{ px: 0.5, pb: 0.75 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  PDF pod KZM (staging Drive)
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {kzmPdfFiles.map((f, i) => (
                    <Chip
                      key={`kzm-${i}-${f.name}`}
                      variant="outlined"
                      size="small"
                      label={f.name}
                      onDelete={() => removeKzmPdfFile(i)}
                    />
                  ))}
                </Box>
              </Box>
            )}
            {contextFiles.length > 0 && (
              <Box sx={{ px: 0.5, pb: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Kontekst rozmowy
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {contextFiles.map((f, i) => (
                    <Chip
                      key={`ctx-${i}-${f.name}`}
                      variant="outlined"
                      size="small"
                      label={f.name}
                      onDelete={() => removeContextFile(i)}
                    />
                  ))}
                </Box>
              </Box>
            )}
            {loading && !gemmaThinking && <LinearProgress sx={{ mt: 0.5 }} />}
          </Box>

          <Box
            sx={{
              flexShrink: 0,
              /* nad obszarem chatu, żeby nic z listy (overflow) nie „najeżdżało” na belkę wejścia */
              position: 'relative',
              zIndex: 1,
              backgroundColor: '#fff',
            }}
          >
            <Divider />
            <Box sx={{ px: 2, pt: 0, pb: 1, mt: 1 }}>
            <input ref={fileInputRef} type="file" hidden multiple accept={provider === 'spark' ? 'image/*,.pdf,.docx' : '.pdf,.docx'} onChange={onPickContextFiles} />
            <input ref={kzmPdfOnlyInputRef} type="file" hidden multiple accept="application/pdf,.pdf" onChange={onPickKzmPdfFiles} />
            <Paper
              elevation={0}
              sx={{
                px: 0,
                py: 0,
                display: 'flex',
                alignItems: 'flex-start',
                borderRadius: '4px',
                backgroundColor: '#F5F5FF',
                border: '1px solid #ededed',
                height: 'auto',
                minHeight: 72,
                width: '100%',
                /* visible: nie obcina delikatnie ringa przy „nagrywam” (VoiceInputButton) ani hover spinacza */
                overflow: 'visible',
                '&:hover': { borderColor: '#B9B9B9' },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  alignSelf: 'stretch',
                  flexShrink: 0,
                  ml: 0.5,
                  width: 36,
                  gap: 0,
                }}
              >
                <Tooltip
                  title={
                    provider === 'spark'
                      ? 'Dodaj plik do kontekstu rozmowy (PDF, DOCX, obrazy). To nie jest ścieżka pod KZM — PDF na KZM dodajesz przyciskiem w wiadomości Gemmy.'
                      : 'Dodaj plik do kontekstu rozmowy'
                  }
                  arrow
                >
                  <Box
                    onClick={() => { if (!loading) fileInputRef.current?.click(); }}
                    sx={{
                      width: 36,
                      height: 36,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: !loading ? '#6B6BD6' : '#B9B9B9',
                      cursor: !loading ? 'pointer' : 'default',
                      userSelect: 'none',
                      borderRadius: '50%',
                      '&:hover': !loading ? { bgcolor: 'rgba(63,63,220,0.08)' } : {},
                    }}
                  >
                    <AttachFileIcon sx={{ fontSize: 20, transform: 'rotate(45deg)' }} />
                  </Box>
                </Tooltip>
                <VoiceInputButton
                  isRecording={voiceDictation.isRecording}
                  isSupported={voiceDictation.isSupported}
                  disabled={loading}
                  onClick={voiceDictation.toggle}
                  size={36}
                  errorText={voiceDictation.error}
                />
              </Box>
              <InputBase
                inputRef={textInputRef}
                placeholder="Napisz wiadomość"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                multiline
                minRows={2}
                maxRows={10}
                disabled={loading}
                sx={{
                  pl: 1,
                  pr: 1,
                  pt: 1,
                  pb: 1,
                  flex: 1,
                  fontSize: 14,
                  '& textarea::placeholder': { whiteSpace: 'pre-wrap' },
                }}
              />
              <Box
                onClick={() => { if (canSend) void send(); }}
                sx={{
                  mr: 0.5,
                  width: 40,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: sendColor,
                  cursor: canSend ? 'pointer' : 'default',
                  userSelect: 'none',
                  alignSelf: 'center',
                }}
              >
                <SendSvg width={20} height={20} />
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>
    </Box>
    </Drawer>
  );
};

export default AIDialog;
